/**
 * Provenance: expression → input closure → sources.
 *
 * This is the architectural move that makes the citation UI trustworthy rather
 * than aspirational. Sources are never attached to a metric by hand; they are
 * derived by walking the expression to the inputs it actually depends on. So
 * "the sources shown under this number are the sources this number depends on"
 * is structurally true, and it cannot drift.
 */

import { parse, refs, type Node } from './expr.js';
import type { Confidence, Input, Pack, Source } from './schema.js';
import { worstConfidence } from './schema.js';
import { inputById } from './scope.js';

export interface Provenance {
  /** Inputs this expression depends on, transitively, in stable order. */
  inputIds: string[];
  /** Deduplicated source ids, in the order their inputs first appear. */
  sourceIds: string[];
  /** Worst confidence in the closure — one placeholder taints the metric. */
  confidence: Confidence;
  /** Minimum sigFigs in the closure. Drives output rounding. */
  sigFigs: number;
  /** Adjustable inputs in the closure — these are the metric's sliders. */
  assumptionIds: string[];
}

/**
 * Inputs currently cannot reference other inputs (values are literal), so the
 * closure is one level deep. The signature is written to allow nesting later
 * without changing callers.
 */
export function closureOf(node: Node, byId: Record<string, Input>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const visit = (ids: string[]) => {
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  };
  visit(refs(node));
  void byId;
  return out;
}

export function provenanceOf(
  expr: string | Node,
  pack: Pack,
  extraInputIds: string[] = [],
): Provenance {
  const node = typeof expr === 'string' ? parse(expr) : expr;
  const byId = inputById(pack);

  const inputIds = [...closureOf(node, byId), ...extraInputIds].filter(
    (id, i, arr) => arr.indexOf(id) === i,
  );

  const sourceIds: string[] = [];
  const seenSources = new Set<string>();
  const confidences: Confidence[] = [];
  const sigFigsList: number[] = [];
  const assumptionIds: string[] = [];

  for (const id of inputIds) {
    const input = byId[id];
    // Scenario params and `t` are not Inputs; they carry no citations of their own.
    if (!input) continue;
    confidences.push(input.confidence);
    sigFigsList.push(input.sigFigs);
    if (input.adjustable) assumptionIds.push(id);
    for (const s of input.sourceIds) {
      if (seenSources.has(s)) continue;
      seenSources.add(s);
      sourceIds.push(s);
    }
  }

  return {
    inputIds,
    sourceIds,
    confidence: confidences.length ? worstConfidence(confidences) : 'placeholder',
    sigFigs: sigFigsList.length ? Math.min(...sigFigsList) : 2,
    assumptionIds,
  };
}

/** Sources grouped by which input they back — how the drawer renders them. */
export interface SourceGroup {
  input: Input;
  sources: Source[];
}

export function groupSourcesByInput(prov: Provenance, pack: Pack): SourceGroup[] {
  const byId = inputById(pack);
  const sourceById: Record<string, Source> = {};
  for (const s of pack.sources) sourceById[s.id] = s;

  const groups: SourceGroup[] = [];
  for (const id of prov.inputIds) {
    const input = byId[id];
    if (!input || input.sourceIds.length === 0) continue;
    const sources = input.sourceIds
      .map((sid) => sourceById[sid])
      .filter((s): s is Source => Boolean(s));
    if (sources.length) groups.push({ input, sources });
  }
  return groups;
}

/** Reverse index: which metrics depend on a given input. Powers /inputs. */
export function dependentsOf(pack: Pack): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const m of pack.metrics) {
    let ids: string[];
    try {
      ids = refs(parse(m.expr));
    } catch {
      continue;
    }
    if (m.series) {
      try {
        ids = [...ids, ...refs(parse(m.series.perYearExpr)), m.series.horizonYearsInput];
      } catch {
        /* validator reports this separately */
      }
    }
    for (const id of new Set(ids)) {
      (out[id] ??= []).push(m.id);
    }
  }
  return out;
}
